import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Spin, Alert } from "antd";
import { useTranslation } from "react-i18next";
// Icons
import { EditFilled, MessageFilled, WechatFilled } from "@ant-design/icons";
// GraphQL
import GET_SURVEY_QUESTION from "graphql/queries/GetSurveyQuestion";
// Styles
import {
  Container,
  AnswersCard,
  Question,
  StyledRadio,
  CardContent,
  UpperCaseHeading,
  ContinueButton,
  IconContainer,
  ResponsiveGrid,
} from "./SingleChoiceQuestionStyle";

type IconMapping = {
  [key: number]: JSX.Element;
};

const iconMapping: IconMapping = {
  19: <EditFilled className="icon" />,
  20: <MessageFilled className="icon" />,
  21: <WechatFilled className="icon" />,
};

interface LearningGoalProps {
  onChange: (data: { goal: number }) => void;
  createSurveyResponse: (surveyQuestionId: number, answerId: number) => void;
}
const LearningGoal = ({
  onChange,
  createSurveyResponse,
}: LearningGoalProps) => {
  const [selectedGoal, setSelectedGoal] = useState<number | null>(null);
  const { t } = useTranslation("registration_survey");

  const { error, loading, data } = useQuery(GET_SURVEY_QUESTION, {
    variables: {
      order: 3,
      surveyType: 0,
    },
  });

  if (loading) return <Spin />;
  if (error) return <Alert message="Error loading question" type="error" />;

  const surveyQuestion = data?.surveyQuestion;

  if (!surveyQuestion)
    return <Alert message="Question not found" type="warning" />;

  const handleSelectionChange = (value: number) => {
    setSelectedGoal(value);
  };

  const handleCardClick = (value: number) => {
    handleSelectionChange(value);
  };

  const handleSubmit = async () => {
    if (selectedGoal) {
      onChange({ goal: selectedGoal });
      await createSurveyResponse(surveyQuestion.id, selectedGoal);
    }
  };

  return (
    <Container>
      <Question>
        <UpperCaseHeading>
          {t(surveyQuestion.questionText.trim())}
        </UpperCaseHeading>
      </Question>
      <AnswersCard bodyStyle={{ padding: 0 }}>
        {surveyQuestion.surveyAnswers.map((answer: any) => (
          <ResponsiveGrid key={answer.id}>
            <CardContent
              onClick={() => handleCardClick(answer.id)}
              isSelected={selectedGoal === answer.id}
            >
              <div className="icon-text-group">
                <IconContainer>{iconMapping[answer.id]}</IconContainer>
                <span>{t(answer.answerText.trim())}</span>
              </div>
              <StyledRadio
                key={answer.id}
                value={answer.id}
                onChange={() => handleSelectionChange(answer.id)}
                checked={selectedGoal === answer.id}
              />
            </CardContent>
          </ResponsiveGrid>
        ))}

        <ContinueButton
          type="primary"
          onClick={handleSubmit}
          disabled={!selectedGoal}
        >
          CONTINUE
        </ContinueButton>
      </AnswersCard>
    </Container>
  );
};

export default LearningGoal;
